<script setup lang="ts">
/**
 * Design System Toast Component
 * Used internally by useDSToast composable
 */

const { toasts, remove } = useDSToast()

const getToastClasses = (type: string) => {
  const baseClasses = 'flex items-start gap-3 p-4 rounded-lg shadow-lg border max-w-sm w-full'

  const typeClasses = {
    success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
    error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200',
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
  }

  return `${baseClasses} ${typeClasses[type as keyof typeof typeClasses]}`
}

const getIconName = (type: string) => {
  const iconMap = {
    success: 'i-lucide-check-circle',
    error: 'i-lucide-x-circle',
    warning: 'i-lucide-alert-triangle',
    info: 'i-lucide-info'
  }
  return iconMap[type as keyof typeof iconMap]
}
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <div class="fixed top-4 right-4 z-50 flex flex-col gap-2" aria-live="polite" aria-atomic="false">
        <TransitionGroup
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 translate-x-8"
          enter-to-class="opacity-100 translate-x-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 translate-x-0"
          leave-to-class="opacity-0 translate-x-8"
        >
          <div
            v-for="toast in toasts"
            :key="toast.id"
            :class="getToastClasses(toast.type)"
            :role="toast.type === 'error' ? 'alert' : 'status'"
          >
            <UIcon :name="getIconName(toast.type)" class="w-5 h-5 flex-shrink-0 mt-0.5" />

            <div class="flex-1 text-sm">
              {{ toast.message }}
            </div>

            <button
              type="button"
              :aria-label="$t('a11y.close')"
              class="flex-shrink-0 hover:opacity-70"
              @click="remove(toast.id)"
            >
              <UIcon name="i-lucide-x" class="w-4 h-4" />
            </button>
          </div>
        </TransitionGroup>
      </div>
    </Teleport>
  </ClientOnly>
</template>
